/**
 * B1022 d.2 — the `/gift-cards/balance` controller: whose page (`?tenant=` or
 * a site key), the venue's facts (one `session.guest` read, for the bar and
 * the theme), then the PUBLIC `giftcard.balance` door — no sign-in, no token.
 * The number is checked at the field (16 digits, Luhn) BEFORE the door is
 * asked, so a typo never costs one of the card's tries. The PIN is cleared
 * after every answer and nothing typed is written anywhere.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useDocumentTitle } from "@transact/ui";
import { ApiError, createApiClient, type ApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import { meVenueFacts, type MeVenueFacts } from "../lib/me.js";
import { venueBrand } from "../lib/membershipsPage.js";
import { paintRememberedTheme, paintVenueTheme } from "../lib/paintVenueTheme.js";
import { createSession, type Session, type StorageLike } from "../lib/session.js";
import { balanceFacts, balanceIssues, cardDigits, type BalanceFacts, type BalanceIssues, type BalanceRoute } from "../lib/giftCardBalance.js";
import { ticketsHref } from "../lib/giftCardsPage.js";
import { GiftCardBalancePage } from "./giftCardBalancePage.js";

const CHECK_FAILED = "The balance couldn't be checked just now. Try again in a moment.";

export function GiftCardBalanceApp(props: {
  readonly route: BalanceRoute;
  readonly apiBaseUrl: string;
  /** Test seams: the API client for a tenant (an anonymous session — the door is public). */
  readonly client?: (tenantId: string, session: Session) => ApiClient;
  /** Accepted for symmetry with the other pages; this page stores nothing. */
  readonly storage?: StorageLike | null;
}): ReactElement {
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown",
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);
  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;
  const anon = (t: string): ApiClient => props.client?.(t, createSession(null)) ?? createApiClient({ baseUrl: props.apiBaseUrl, tenantId: t });

  const [venue, setVenue] = useState<MeVenueFacts>({});
  const [number, setNumber] = useState("");
  const [pin, setPin] = useState("");
  const [issues, setIssues] = useState<BalanceIssues>({});
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<BalanceFacts | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined") paintRememberedTheme();
  }, []);
  useEffect(() => {
    if (typeof scope !== "object") return;
    let gone = false;
    void anon(scope.tenantId)
      .call("session.guest", { channelId: scope.channelId })
      .then((res) => {
        if (gone) return;
        const facts = meVenueFacts(res);
        setVenue(facts);
        if (typeof document !== "undefined") paintVenueTheme(facts.theme);
      })
      .catch(() => undefined /* the bar falls back to the page's own words */);
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one read per scope
  }, [scope]);

  const brand = venueBrand(venue, tenantId, { doorShut: false });
  useDocumentTitle(brand.businessName !== undefined ? `Gift card balance — ${brand.businessName}` : "Gift card balance");

  const check = async (): Promise<void> => {
    if (tenantId === undefined || busy) return;
    const found = balanceIssues({ number, pin });
    setIssues(found);
    setRefusal(undefined);
    /* the refused fields say why; installRefusalFocus (main.tsx) hands the keyboard to the first */
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setResult(undefined);
    try {
      const res = await anon(tenantId).call("giftcard.balance", { number: cardDigits(number), pin: pin.trim() });
      const facts = balanceFacts(res, { timeZone: venue.timezone });
      if (facts === undefined) setRefusal(CHECK_FAILED);
      else setResult(facts);
    } catch (err) {
      setRefusal(err instanceof ApiError && err.message.trim() !== "" && err.status < 500 ? err.message : CHECK_FAILED);
    } finally {
      setPin("");
      setBusy(false);
    }
  };

  return (
    <GiftCardBalancePage
      venue={brand}
      ticketsUrl={tenantId !== undefined ? ticketsHref(tenantId) : undefined}
      number={number}
      pin={pin}
      issues={issues}
      onNumber={(v) => {
        setNumber(v);
        setIssues((i) => ({ ...(i.pin !== undefined ? { pin: i.pin } : {}) }));
      }}
      onPin={(v) => {
        setPin(v);
        setIssues((i) => ({ ...(i.number !== undefined ? { number: i.number } : {}) }));
      }}
      onCheck={() => void check()}
      busy={busy}
      refusal={scope === "unknown" ? "This page doesn't know whose gift card to check. Open it from the business's own site." : refusal}
      result={result}
    />
  );
}
